import React, { useState, useEffect, useRef } from "react";
import "./css/TextEditor.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button, Dropdown } from "react-bootstrap";
import deleteicn from "../assets/deleteicn.svg";
import deleteRed from "../assets/deleteRed.svg";
import Down from "../assets/down-arrow.png";
import rightMArk from "../assets/rightMArk.svg";
import HeaderTextEditor from "./headerTextEditor";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { formatDistanceToNow } from "date-fns";
import DOMPurify from "dompurify";
import $ from "jquery";
import { BrainCircuit } from "lucide-react";

const TextEditor = () => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [notes, setNotes] = useState([]);
  const [activeNote, setActiveNote] = useState(null);
  const [lineNumber, setLineNumber] = useState(0);
  const [columnNumber, setColumnNumber] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [editorFocused, setEditorFocused] = useState(false);
  const [history, setHistory] = useState([]);
  const [showResult, setShowResult] = useState(false);

  const fonts_list = [
    { family: "Arial", value: "Arial" },
    { family: "Courier", value: "Courier-New" },
    { family: "Georgia", value: "Georgia" },
    { family: "Verdana", value: "Verdana" },
  ];

  const truncatedFontsList = fonts_list.map((font) => {
    const truncatedLabel =
      font.family.split(" ").slice(0, 1).join(" ") +
      (font.family.split(" ").length > 1 ? "..." : "");
    return { ...font, label: truncatedLabel };
  });

  const [showFindReplaceModal, setShowFindReplaceModal] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [findCount, setFindCount] = useState(0);
  const [replaceCount, setReplaceCount] = useState(0);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showSpecialCharacterModal, setShowSpecialCharacterModal] =
    useState(false);
  const [showEmojiModal, setShowEmojiModal] = useState(false);
  const [selectedSpecialCharacter, setSelectedSpecialCharacter] = useState("");
  const [selectedEmoji, setSelectedEmoji] = useState("");
  const [isStatusBarVisible, setIsStatusBarVisible] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSelected, setIsSelected] = useState(false);
  const [showAiResultModal, setShowAiResultModal] = useState(false);

  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [editorContent, setEditorContent] = useState("");

  const [fonts, setFonts] = useState([]);
  const [spellCheckEnabled, setSpellCheckEnabled] = useState(true);

  const textareaRef = useRef();
  const fileInputRef = useRef(null);
  const titleRef = useRef(null);
  const searchRef = useRef(null);

  // ============ HELPER FUNCTIONS (MUST BE DEFINED BEFORE detectAI) ============

  const calculateVariance = (numbers) => {
    if (!numbers || numbers.length === 0) return 0;
    const mean = numbers.reduce((a, b) => a + b, 0) / numbers.length;
    const variance =
      numbers.reduce((sum, num) => sum + Math.pow(num - mean, 2), 0) /
      numbers.length;
    return variance;
  };

  const detectRepetitivePhrases = (text) => {
    const phrases = text
      .toLowerCase()
      .split(/[.!?]/)
      .filter((p) => p.trim());
    const phraseSet = new Set(phrases);
    return phrases.length - phraseSet.size;
  };

  const detectPassiveVoice = (text) => {
    const passivePattern = /\b(was|were|been|being|is|are|am)\s+\w+ed\b/gi;
    const matches = text.match(passivePattern);
    return matches ? matches.length : 0;
  };

  const analyzePunctuation = (text) => {
    const sentences = text.split(/[.!?]+/).filter((s) => s.trim());
    let perfectCount = 0;

    sentences.forEach((sentence) => {
      const trimmed = sentence.trim();
      if (trimmed && /^[A-Z]/.test(trimmed)) {
        perfectCount++;
      }
    });

    return sentences.length > 0 ? perfectCount / sentences.length : 0;
  };

  const calculatePerplexity = (text) => {
    const words = text.toLowerCase().split(/\s+/);
    const uniqueWords = new Set(words);

    const commonAIWords = [
      "the",
      "is",
      "to",
      "and",
      "a",
      "of",
      "in",
      "that",
      "it",
      "for",
    ];
    const commonWordCount = words.filter((w) =>
      commonAIWords.includes(w)
    ).length;
    const commonWordRatio = commonWordCount / words.length;

    return (uniqueWords.size / words.length) * 100 * (1 - commonWordRatio);
  };

  const calculateLexicalDiversity = (words) => {
    if (!words || words.length === 0) return 0;
    const uniqueWords = new Set(words.map((w) => w.toLowerCase()));
    return uniqueWords.size / words.length;
  };

  const analyzeSentenceStarts = (sentences) => {
    if (!sentences || sentences.length === 0) return 0;
    const starts = sentences
      .map((s) => {
        const trimmed = s.trim();
        const firstWord = trimmed.split(/\s+/)[0];
        return firstWord ? firstWord.toLowerCase() : "";
      })
      .filter((s) => s);

    if (starts.length === 0) return 0;
    const uniqueStarts = new Set(starts);
    return uniqueStarts.size / starts.length;
  };

  const calculateTransitionDensity = (text, words) => {
    if (!words || words.length === 0) return 0;
    const transitions = [
      "however",
      "therefore",
      "furthermore",
      "moreover",
      "additionally",
      "consequently",
      "nevertheless",
      "nonetheless",
      "thus",
      "hence",
      "accordingly",
      "meanwhile",
      "subsequently",
      "likewise",
      "similarly",
    ];

    const transitionCount = words.filter((w) =>
      transitions.includes(w.toLowerCase().replace(/[.,!?;:]/g, ""))
    ).length;

    return transitionCount / words.length;
  };

  const calculateConjunctionDensity = (text, words) => {
    if (!words || words.length === 0) return 0;
    const conjunctions = [
      "and",
      "but",
      "or",
      "nor",
      "for",
      "yet",
      "so",
      "although",
      "because",
      "since",
      "unless",
      "while",
    ];

    const conjunctionCount = words.filter((w) =>
      conjunctions.includes(w.toLowerCase().replace(/[.,!?;:]/g, ""))
    ).length;

    return conjunctionCount / words.length;
  };

  const calculateAdverbDensity = (text, words) => {
    if (!words || words.length === 0) return 0;
    const adverbPattern = /ly\b/i;
    const adverbCount = words.filter((w) => adverbPattern.test(w)).length;
    return adverbCount / words.length;
  };

  const getRiskLevel = (score) => {
    if (score >= 75) return "CRITICAL";
    if (score >= 60) return "HIGH";
    if (score >= 42) return "MEDIUM";
    if (score >= 25) return "LOW";
    return "MINIMAL";
  };

  const getVerdict = (score) => {
    if (score >= 85) return "Almost certainly AI-generated";
    if (score >= 70) return "Very likely AI-generated";
    if (score >= 55) return "Likely AI-generated";
    if (score >= 42) return "Possibly AI-generated";
    if (score >= 25) return "Possibly human-written";
    if (score >= 15) return "Likely human-written";
    return "Almost certainly human-written";
  };

  const stripHtmlTags = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  // ============ ULTRA-ADVANCED AI DETECTION FUNCTION ============
  const detectAI = async (inputText) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          const words = inputText.trim().split(/\s+/);
          const wordCount = words.length;

          if (wordCount < 5) {
            reject(new Error("Text too short. Please enter at least 5 words."));
            return;
          }

          // ===== ENHANCED AI PATTERN DETECTION =====
          const aiPatterns = [
            {
              pattern:
                /\b(furthermore|moreover|additionally|subsequently|consequently|nevertheless|nonetheless|henceforth|thereby|wherein|whereby|inasmuch|insofar|notwithstanding)\b/gi,
              weight: 16,
              category: "formal_transitions",
            },
            {
              pattern:
                /\b(comprehensive|multifaceted|optimize|leverage|utilize|facilitate|implement|paradigm|synergy|holistic|robust|scalable|cutting-edge|state-of-the-art|innovative|transformative|revolutionary|groundbreaking|pioneering|unprecedented|strategic|tactical|instrumental|integral|quintessential|paramount)\b/gi,
              weight: 20,
              category: "buzzwords",
            },
            {
              pattern:
                /\b(demonstrate|illustrate|exemplify|elucidate|ascertain|regarding|concerning|pertaining|with respect to|in terms of|vis-à-vis|notwithstanding|heretofore|hitherto|aforementioned|underscore|undersigned)\b/gi,
              weight: 14,
              category: "academic",
            },
            {
              pattern:
                /\b(it is important to note|it should be noted|it is worth mentioning|as previously mentioned|in conclusion|to summarize|in summary|on the other hand|first and foremost|last but not least|it goes without saying|needless to say|by and large|for the most part|at the end of the day)\b/gi,
              weight: 24,
              category: "generic_phrases",
            },
            {
              pattern:
                /\b(seamless|effortless|streamlined|dynamic|versatile|diverse|various|numerous|significant|substantial|considerable|remarkable|notable|exceptional|outstanding|superior|optimal|ultimate|essential|crucial|vital|critical|profound|intricate|nuanced|sophisticated)\b/gi,
              weight: 12,
              category: "descriptors",
            },
            {
              pattern:
                /\b(which|whom|whose|wherein|whereby|thus|hence|therefore|accordingly|henceforth|hereafter|therein|thereof)\b/gi,
              weight: 8,
              category: "formal_pronouns",
            },
            {
              pattern:
                /\b(typically|generally|usually|often|frequently|commonly|predominantly|largely|mostly|primarily|essentially|fundamentally|basically|invariably|consistently|universally)\b/gi,
              weight: 10,
              category: "hedging",
            },
            {
              pattern:
                /\b(The key to|One of the most|In today's world|In the modern era|It is widely recognized|Research shows that|Studies indicate|Evidence suggests|Experts agree|It has been observed|It can be argued)\b/gi,
              weight: 18,
              category: "sentence_starters",
            },
            {
              pattern:
                /\b(in order to|due to the fact that|for the purpose of|in the event that|at this point in time|on a regular basis|make use of|give consideration to|take into account|bear in mind)\b/gi,
              weight: 14,
              category: "redundancy",
            },
            {
              pattern:
                /\b(In conclusion|To conclude|To sum up|In summary|Overall|Ultimately|In essence|At the end of the day|When all is said and done|All things considered|Taking everything into account)\b/gi,
              weight: 17,
              category: "conclusions",
            },
            {
              pattern:
                /\b(delve|delving|navigate|navigating|landscape|realm|sphere|domain|facet|nuance|myriad|plethora|array|spectrum)\b/gi,
              weight: 19,
              category: "ai_favorites",
            },
            {
              pattern:
                /\b(meticulous|meticulously|intricacies|juxtaposition|pivotal|transformative|unprecedented|unparalleled|game-changing|revolutionize)\b/gi,
              weight: 17,
              category: "overused_adjectives",
            },
          ];

          // ===== ADVANCED LINGUISTIC ANALYSIS =====
          const sentences = inputText.split(/[.!?]+/).filter((s) => s.trim());
          const sentenceLengths = sentences.map(
            (s) => s.trim().split(/\s+/).length
          );

          const avgSentenceLength =
            sentenceLengths.length > 0
              ? sentenceLengths.reduce((a, b) => a + b, 0) /
                sentenceLengths.length
              : 0;

          const sentenceLengthVariance = calculateVariance(sentenceLengths);
          const stdDeviation = Math.sqrt(sentenceLengthVariance);

          const complexWordRatio =
            words.filter((word) => word.length > 7).length / wordCount;
          const veryComplexWordRatio =
            words.filter((word) => word.length > 10).length / wordCount;
          const repetitivePhrases = detectRepetitivePhrases(inputText);
          const passiveVoiceCount = detectPassiveVoice(inputText);
          const perfectPunctuationScore = analyzePunctuation(inputText);
          const perplexityScore = calculatePerplexity(inputText);
          const burstiness = sentenceLengthVariance / (avgSentenceLength || 1);
          const lexicalDiversity = calculateLexicalDiversity(words);
          const sentenceStartVariety = analyzeSentenceStarts(sentences);
          const transitionWordDensity = calculateTransitionDensity(
            inputText,
            words
          );
          const conjunctionDensity = calculateConjunctionDensity(
            inputText,
            words
          );
          const adverbDensity = calculateAdverbDensity(inputText, words);

          let aiScore = 0;
          const foundPatterns = [];
          const matchedWordsWithPositions = [];
          const categoryScores = {};

          // Pattern matching with position tracking
          aiPatterns.forEach(({ pattern, weight, category }) => {
            const matches = [...inputText.matchAll(pattern)];
            matches.forEach((match) => {
              aiScore += weight;
              foundPatterns.push(match[0]);

              matchedWordsWithPositions.push({
                word: match[0],
                start: match.index,
                end: match.index + match[0].length,
                weight: weight,
                category: category,
              });

              categoryScores[category] =
                (categoryScores[category] || 0) + weight;
            });
          });

          // ===== ADVANCED SCORING ADJUSTMENTS =====
          // Sentence length analysis
          if (avgSentenceLength > 25) aiScore += 20;
          if (avgSentenceLength > 30) aiScore += 14;
          if (avgSentenceLength > 35) aiScore += 10;
          if (avgSentenceLength > 40) aiScore += 8;
          if (avgSentenceLength < 10) aiScore -= 18;
          if (avgSentenceLength < 8) aiScore -= 12;
          if (avgSentenceLength < 6) aiScore -= 8;

          // Burstiness (variance in sentence length)
          if (burstiness < 0.2) aiScore += 30;
          if (burstiness < 0.3) aiScore += 18;
          if (burstiness < 0.4) aiScore += 12;
          if (burstiness > 1.2) aiScore -= 12;
          if (burstiness > 1.5) aiScore -= 18;
          if (burstiness > 2.0) aiScore -= 10;

          // Standard deviation
          if (stdDeviation < 2) aiScore += 25;
          if (stdDeviation < 4) aiScore += 15;
          if (stdDeviation < 6) aiScore += 8;

          // Complex word usage
          if (complexWordRatio > 0.35) aiScore += 24;
          if (complexWordRatio > 0.45) aiScore += 20;
          if (complexWordRatio > 0.55) aiScore += 16;
          if (veryComplexWordRatio > 0.15) aiScore += 18;
          if (veryComplexWordRatio > 0.2) aiScore += 14;

          // Repetitive phrases
          if (repetitivePhrases > 3) aiScore += 20;
          if (repetitivePhrases > 5) aiScore += 15;
          if (repetitivePhrases > 7) aiScore += 10;

          // Passive voice
          const passiveRatio = passiveVoiceCount / sentences.length;
          if (passiveRatio > 0.3) aiScore += 18;
          if (passiveRatio > 0.4) aiScore += 14;
          if (passiveRatio > 0.5) aiScore += 12;
          if (passiveRatio > 0.6) aiScore += 10;

          // Perfect punctuation
          if (perfectPunctuationScore > 0.95) aiScore += 18;
          if (perfectPunctuationScore > 0.98) aiScore += 12;
          if (perfectPunctuationScore === 1.0) aiScore += 10;

          // Perplexity (predictability)
          if (perplexityScore < 25) aiScore += 24;
          if (perplexityScore < 20) aiScore += 18;
          if (perplexityScore < 15) aiScore += 14;
          if (perplexityScore < 10) aiScore += 10;
          if (perplexityScore > 50) aiScore -= 12;
          if (perplexityScore > 60) aiScore -= 8;

          // Lexical diversity
          if (lexicalDiversity < 0.45) aiScore += 22;
          if (lexicalDiversity < 0.35) aiScore += 18;
          if (lexicalDiversity < 0.25) aiScore += 14;
          if (lexicalDiversity > 0.7) aiScore -= 12;
          if (lexicalDiversity > 0.8) aiScore -= 8;

          // Sentence start variety
          if (sentenceStartVariety < 0.55) aiScore += 18;
          if (sentenceStartVariety < 0.45) aiScore += 14;
          if (sentenceStartVariety < 0.35) aiScore += 10;

          // Transition word density
          if (transitionWordDensity > 0.08) aiScore += 22;
          if (transitionWordDensity > 0.12) aiScore += 18;
          if (transitionWordDensity > 0.15) aiScore += 14;

          // Conjunction density
          if (conjunctionDensity > 0.05) aiScore += 14;
          if (conjunctionDensity > 0.08) aiScore += 12;
          if (conjunctionDensity > 0.1) aiScore += 10;

          // Adverb density
          if (adverbDensity > 0.06) aiScore += 18;
          if (adverbDensity > 0.09) aiScore += 14;
          if (adverbDensity > 0.12) aiScore += 10;

          // Category diversity
          const categoryCount = Object.keys(categoryScores).length;
          if (categoryCount >= 6) aiScore += 25;
          if (categoryCount >= 5) aiScore += 20;
          if (categoryCount >= 4) aiScore += 15;
          if (categoryCount >= 3) aiScore += 10;

          // Normalize score
          aiScore = Math.min(Math.max(aiScore, 0), 100);

          // ===== CONFIDENCE CALCULATION =====
          const textLengthFactor = Math.min(wordCount / 100, 1) * 25;
          const patternDensity = (foundPatterns.length / wordCount) * 100;
          const diversityFactor = (1 - lexicalDiversity) * 20;
          const categoryFactor = categoryCount * 5;

          const confidence = Math.min(
            45 +
              textLengthFactor +
              patternDensity * 2.5 +
              diversityFactor +
              categoryFactor,
            99
          );

          // ===== DETAILED ANALYSIS =====
          const analysis = {
            avgSentenceLength: avgSentenceLength.toFixed(1),
            burstiness: burstiness.toFixed(3),
            stdDeviation: stdDeviation.toFixed(2),
            complexWordRatio: (complexWordRatio * 100).toFixed(1) + "%",
            veryComplexWordRatio: (veryComplexWordRatio * 100).toFixed(1) + "%",
            passiveVoiceCount,
            passiveVoiceRatio: (passiveRatio * 100).toFixed(1) + "%",
            repetitivePhrases,
            perplexityScore: perplexityScore.toFixed(2),
            lexicalDiversity: lexicalDiversity.toFixed(3),
            sentenceStartVariety: sentenceStartVariety.toFixed(3),
            transitionDensity: (transitionWordDensity * 100).toFixed(2) + "%",
            conjunctionDensity: (conjunctionDensity * 100).toFixed(2) + "%",
            adverbDensity: (adverbDensity * 100).toFixed(2) + "%",
            categoryScores,
            patternDensity: patternDensity.toFixed(2) + "%",
            categoryCount,
          };

          resolve({
            isAI: aiScore > 42,
            probability: Math.round(aiScore),
            confidence: Math.round(confidence),
            wordCount,
            sentenceCount: sentences.length,
            foundPatterns,
            matchedWordsWithPositions,
            analysis,
            verdict: getVerdict(aiScore),
            riskLevel: getRiskLevel(aiScore),
          });
        } catch (err) {
          reject(new Error("Analysis failed. Please try again."));
        }
      }, 1800);
    });
  };

  // Handle AI Detection
  const handleAiDetector = async () => {
    if (!editorContent || !editorContent.trim()) {
      setError("Please enter some text to analyze");
      return;
    }

    const plainText = stripHtmlTags(editorContent);

    if (plainText.trim().split(/\s+/).length < 5) {
      setError("Please enter at least 5 words of content");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setShowResult(false);

    try {
      const detection = await detectAI(plainText);
      setResult(detection);
      setShowResult(true);
      setShowAiResultModal(true);

      if (detection.isAI && detection.matchedWordsWithPositions) {
        setTimeout(() => {
          highlightAIWords(detection);
        }, 100);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const highlightAIWords = (result) => {
    if (!result || !result.matchedWordsWithPositions || !textareaRef.current) {
      return;
    }

    const editor = textareaRef.current.getEditor();
    const plainText = stripHtmlTags(editorContent);

    const sortedMatches = [...result.matchedWordsWithPositions].sort(
      (a, b) => b.start - a.start
    );

    sortedMatches.forEach((match) => {
      const quillPosition = getQuillPositionFromPlainText(
        editor,
        plainText.substring(0, match.start)
      );

      if (quillPosition !== -1) {
        editor.formatText(
          quillPosition,
          match.word.length,
          "background",
          "#ffeb3b"
        );
        editor.formatText(quillPosition, match.word.length, "color", "#d32f2f");
        editor.formatText(quillPosition, match.word.length, "bold", true);
      }
    });
  };

  const getQuillPositionFromPlainText = (editor, textBefore) => {
    const editorText = editor.getText();
    let position = 0;
    let plainTextCount = 0;

    while (position < editorText.length && plainTextCount < textBefore.length) {
      if (editorText[position] === textBefore[plainTextCount]) {
        plainTextCount++;
      }
      position++;
    }

    return plainTextCount === textBefore.length ? position - 1 : -1;
  };

  const clearHighlights = () => {
    if (!textareaRef.current) return;
    const editor = textareaRef.current.getEditor();
    const length = editor.getLength();
    editor.removeFormat(0, length);
  };

  useEffect(() => {
    let timer;
    if (showResult && result) {
      timer = setTimeout(() => {
        setShowResult(false);
        setResult(null);
        clearHighlights();
      }, 15000);
    }
    return () => clearTimeout(timer);
  }, [showResult, result]);

  // ============ REST OF THE ORIGINAL CODE ============

  const getAllNotes = () => {
    const notes = JSON.parse(localStorage.getItem("notesapp-notes") || "[]");
    return notes.sort((a, b) => new Date(b.updated) - new Date(a.updated));
  };

  useEffect(() => {
    if (activeNote) {
      setEditorContent(activeNote.body);
      setHistory([]);
    }
    const Fontcustom = ReactQuill.Quill.import("formats/font");
    Fontcustom.whitelist = fonts_list.map((font) => font.value);
    ReactQuill.Quill.register(Fontcustom, true);
  }, [activeNote?.id]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 700) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen((prevState) => !prevState);
  };

  const handlePaste = async (event) => {
    if (!textareaRef.current) return;
    const editor = textareaRef.current.getEditor();
    if (!editor) return;
    event.preventDefault();

    try {
      const clipboardData = event.clipboardData || window.clipboardData;
      let htmlContent = clipboardData.getData("text/html");
      let plainText = clipboardData.getData("text/plain");
      let contentToInsert;

      if (htmlContent) {
        contentToInsert = processHtmlFormatting(htmlContent);
      } else if (plainText) {
        contentToInsert = plainText.replace(/[\*#]/g, "");
      } else {
        return;
      }

      const range = editor.getSelection();
      if (range) {
        if (range.length > 0) {
          editor.deleteText(range.index, range.length);
        }

        if (htmlContent) {
          editor.clipboard.dangerouslyPasteHTML(range.index, contentToInsert);
        } else {
          editor.insertText(range.index, contentToInsert);
        }

        const newPosition =
          range.index +
          (typeof contentToInsert === "string" ? contentToInsert.length : 0);
        editor.setSelection(newPosition, 0);
      }
    } catch (error) {
      console.error("Error in handlePaste:", error);
    }
  };

  const processHtmlFormatting = (htmlContent) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const removeUnwantedChars = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = node.textContent.replace(/[\*#]/g, "");
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        Array.from(node.childNodes).forEach(removeUnwantedChars);
      }
    };

    removeUnwantedChars(tempDiv);
    let processedHtml = tempDiv.innerHTML;
    processedHtml = processedHtml
      .replace(/<p[^>]*><\/p>/gi, "")
      .replace(/\s+/g, " ")
      .replace(/style="[^"]*"/gi, "");

    return processedHtml;
  };

  const handleKeyDown = async (event) => {
    if (event.ctrlKey && event.key === "v") {
      event.preventDefault();

      try {
        const clipboardItems = await navigator.clipboard.read();

        for (const clipboardItem of clipboardItems) {
          if (clipboardItem.types.includes("text/html")) {
            const htmlBlob = await clipboardItem.getType("text/html");
            const htmlContent = await htmlBlob.text();
            const processedContent = processHtmlFormatting(htmlContent);
            insertFormattedContent(processedContent, true);
            return;
          } else if (clipboardItem.types.includes("text/plain")) {
            const textBlob = await clipboardItem.getType("text/plain");
            const textContent = await textBlob.text();
            const cleanedContent = textContent.replace(/[\*#]/g, "");
            insertFormattedContent(cleanedContent, false);
            return;
          }
        }
      } catch (error) {
        try {
          const pastedContent = await navigator.clipboard.readText();
          const cleanedContent = pastedContent.replace(/[\*#]/g, "");
          insertFormattedContent(cleanedContent, false);
        } catch (fallbackError) {
          console.error("Failed to read clipboard contents: ", fallbackError);
        }
      }
    }
  };

  const insertFormattedContent = (content, isHtml) => {
    if (!textareaRef.current) return;
    const editor = textareaRef.current.getEditor();
    if (!editor) return;

    const range = editor.getSelection();
    if (range) {
      if (range.length > 0) {
        editor.deleteText(range.index, range.length);
      }

      if (isHtml) {
        editor.clipboard.dangerouslyPasteHTML(range.index, content);
      } else {
        editor.insertText(range.index, content);
        editor.setSelection(range.index + content.length, 0);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const selectAllText = (element) => {
    if (element) {
      element.select();
    }
  };

  const handleKeyboardShortcuts = (event) => {
    const focusedElement = document.activeElement;

    if (event.ctrlKey) {
      if (
        focusedElement === titleRef.current ||
        focusedElement === searchRef.current
      ) {
        if (event.key === "d") {
          event.preventDefault();
          handleFileSave();
        } else if (event.key === "p") {
          handlePrint();
        } else if (event.key === "x") {
          event.preventDefault();
          handleCut();
        } else if (event.key === "c") {
          handleCopy();
        } else if (event.key === "r") {
          handleFindAndReplace();
        } else if (event.key === "f") {
          event.preventDefault();
          toggleFullScreen();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          handleIncreaseFontSize();
        } else if (event.key === "ArrowLeft") {
          event.preventDefault();
          handleDecreaseFontSize();
        } else if (event.key === "a") {
          event.preventDefault();
          selectAllText(focusedElement);
        }
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyboardShortcuts);
    return () => {
      document.removeEventListener("keydown", handleKeyboardShortcuts);
    };
  }, [isFullScreen]);

  const saveNote = (noteToSave) => {
    const notes = getAllNotes();
    const existing = notes.find((note) => note.id === noteToSave.id);

    if (existing) {
      existing.title = noteToSave.title || "";
      existing.body = noteToSave.body || "";
      existing.updated = new Date().toISOString();
    } else {
      noteToSave.updated = new Date().toISOString();
      notes.push(noteToSave);
    }

    localStorage.setItem("notesapp-notes", JSON.stringify(notes));
  };

  const deleteNote = (id) => {
    const notes = getAllNotes();
    const newNotes = notes.filter((note) => note.id !== id);
    localStorage.setItem("notesapp-notes", JSON.stringify(newNotes));
  };

  useEffect(() => {
    refreshNotes();
  }, []);

  const refreshNotes = () => {
    let notes = getAllNotes();

    if (notes.length === 0) {
      const defaultNote = {
        id: Math.floor(Math.random() * 1000000),
        title: "",
        body: "",
      };
      saveNote(defaultNote);
      notes = getAllNotes();
    }

    setNotes(notes);

    if (notes.length > 0) {
      setActiveNote(notes[0]);
    }
  };

  const handleIncreaseFontSize = () => {
    const editor = textareaRef.current?.getEditor();
    if (editor) {
      const currentSize = parseInt(
        window.getComputedStyle(editor.root).fontSize
      );
      editor.format("size", `${currentSize + 1}px`);
    }
  };

  const handleDecreaseFontSize = () => {
    const editor = textareaRef.current?.getEditor();
    if (editor) {
      const currentSize = parseInt(
        window.getComputedStyle(editor.root).fontSize
      );
      editor.format("size", `${currentSize - 1}px`);
    }
  };

  const handleNoteSelect = (noteId) => {
    const selectedNote = filteredNotes.find((note) => note.id === noteId);

    if (selectedNote && selectedNote.id !== activeNote?.id) {
      if (activeNote) {
        saveNote({
          id: activeNote.id,
          title: activeNote.title || "",
          body: editorContent,
        });
      }

      setActiveNote(selectedNote);
      setEditorContent(selectedNote.body);
      setHistory([]);
      setHistoryIndex(-1);

      setTimeout(() => {
        const updatedNotes = getAllNotes();
        setNotes(updatedNotes);
      }, 0);
    }
  };

  const handleNoteAdd = () => {
    const newNote = {
      id: Math.floor(Math.random() * 1000000),
      title: "",
      body: "<p><br></p>",
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
    };

    saveNote(newNote);
    setActiveNote(newNote);
    refreshNotes();
    setEditorContent("");
  };

  const handleNoteEdit = (title, body) => {
    const sanitizedBody = DOMPurify.sanitize(body);

    saveNote({
      id: activeNote.id,
      title: title || "",
      body: sanitizedBody,
    });

    refreshNotes();
    updateEditorInfo(sanitizedBody);
    setEditorContent(sanitizedBody);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const editor = textareaRef.current.getEditor();
        const range = editor.getSelection();
        if (range) {
          const imageUrl = reader.result;
          editor.insertEmbed(range.index, "image", imageUrl);
          setTimeout(() => ImageResizeAndDrag(), 0);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const ImageResizeAndDrag = () => {
    const images = document.querySelectorAll(".resizable-draggable");
    images.forEach((img) => {
      img.addEventListener("mousedown", onMouseDown);

      function onMouseDown(e) {
        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = parseInt(
          document.defaultView.getComputedStyle(img).width,
          10
        );
        const startHeight = parseInt(
          document.defaultView.getComputedStyle(img).height,
          10
        );

        function onMouseMove(e) {
          img.style.width = startWidth + (e.clientX - startX) + "px";
          img.style.height = startHeight + (e.clientY - startY) + "px";
        }

        function onMouseUp() {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
        }

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      }
    });
  };

  const handleNoteDelete = (noteId) => {
    setNoteToDelete(noteId);
    setShowModal(true);
  };

  const confirmDelete = () => {
    if (noteToDelete !== null) {
      deleteNote(noteToDelete);
      refreshNotes();
      setShowModal(false);
      setNoteToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowModal(false);
    setNoteToDelete(null);
  };

  const updateEditorInfo = (body) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = body;
    const text = tempDiv.innerText || tempDiv.textContent;
    const words = text.split(/\s+/).filter(Boolean).length;
    setWordCount(words);
  };

  const handleCursorPosition = () => {
    const editor = textareaRef.current?.getEditor();
    if (editor) {
      const selection = editor.getSelection();
      if (selection) {
        const { index } = selection;
        const text = editor.getText();
        const lines = text.substring(0, index).split("\n");
        const lineNumber = lines.length;
        const columnNumber = lines[lines.length - 1].length;

        setLineNumber(lineNumber);
        setColumnNumber(columnNumber);
      }
    }
  };

  useEffect(() => {
    handleCursorPosition();
  }, [editorContent]);

  const handleEditorFocus = () => {
    setEditorFocused(true);
  };

  const handleEditorBlur = () => {
    setEditorFocused(false);
  };

  const filteredNotes = notes.filter((note) =>
    note.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const renderNoteList = () =>
    filteredNotes.map((note) => {
      const displayTitle = note.title.trim() === "" ? "Untitled" : note.title;
      const plainBody = stripHtmlTags(note.body);
      const displayBody =
        plainBody.trim() === ""
          ? "Write Something"
          : truncateText(plainBody, 10);

      return (
        <div
          key={note.id}
          className={`notes__list-item ${
            activeNote && activeNote.id === note.id
              ? "notes__list-item--selected"
              : ""
          }`}
          onClick={() => handleNoteSelect(note.id)}
          onDoubleClick={() => handleNoteDelete(note.id)}
        >
          <div className="notes__small-title">{displayTitle}</div>
          <div className="notes__small-preview">{displayBody}</div>
          <div className="notes__small-updated">
            {formatDistanceToNow(new Date(note.updated), { addSuffix: true })}
          </div>
        </div>
      );
    });

  const triggerFileInputClick = () => {
    document.getElementById("fileInput").click();
  };

  const handleFileOpen = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const lines = content.split("\n");
        const title = lines[0] || "Untitled Note";
        const body = lines.slice(1).join("\n");
        handleNoteEdit(title, body);
      };
      reader.readAsText(file);
    }
  };

  const renderNotePreview = () => {
    if (!activeNote) {
      return (
        <div className="notes__preview" style={{ visibility: "hidden" }} />
      );
    }

    return (
      <>
        <div className="notes__preview">
          <div className="titleText">
            <div className="inputHeadingleft">
              <input
                ref={titleRef}
                className="notes__title"
                type="text"
                placeholder="Untitled Note"
                value={activeNote.title}
                onChange={(e) =>
                  handleNoteEdit(e.target.value, activeNote.body)
                }
              />
            </div>
            <div className="deleteBtn">
              <button onClick={() => handleNoteDelete(activeNote.id)}>
                <img
                  src={hovered ? deleteRed : deleteicn}
                  alt="Delete Icon"
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                />
              </button>
            </div>
          </div>

          <div className="textAreraSectiom">
            <ReactQuill
              ref={textareaRef}
              value={editorContent}
              onChange={(content) => {
                setEditorContent(content);
                handleNoteEdit(activeNote ? activeNote.title : "", content);
              }}
              onFocus={handleEditorFocus}
              onBlur={handleEditorBlur}
              onPaste={handlePaste}
              placeholder="Write something..."
              theme="snow"
              modules={{
                toolbar: [
                  [{ font: truncatedFontsList.map((font) => font.value) }],
                  [{ size: ["small", false, "large", "huge"] }],
                  [{ header: [1, 2, 3, 4, 5, 6, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ color: [] }, { background: [] }],
                  ["blockquote"],
                  [{ align: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  [{ indent: "-1" }, { indent: "+1" }],
                  [{ lineHeight: ["1", "1.15", "1.5", "2", "2.5", "3"] }],
                  [{ direction: "rtl" }],
                  ["clean"],
                ],
                clipboard: {
                  matchVisual: false,
                },
              }}
              formats={[
                "font",
                "size",
                "bold",
                "italic",
                "underline",
                "strike",
                "color",
                "background",
                "blockquote",
                "script",
                "header",
                "list",
                "bullet",
                "indent",
                "align",
                "direction",
                "lineHeight",
              ]}
              id="editor"
            />
          </div>

          {editorFocused && (
            <div className="editor-info statusbar">
              <div className="LineCounter">
                <label>Line: {lineNumber}</label>
                <label>Column: {columnNumber}</label>
              </div>
              <div className="columnCounter">
                <label>Words: {wordCount}</label>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const handleFileSave = () => {
    if (activeNote) {
      const blob = new Blob([`${activeNote.title}\n${activeNote.body}`], {
        type: "text/plain;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeNote.title || "Untitled Note"}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handlePrint = () => {
    if (activeNote) {
      const printWindow = window.open("", "", "width=800,height=600");
      printWindow.document.open();
      printWindow.document.write(`
      <html>
        <head>
          <title>Print Note</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
          </style>
        </head>
        <body>
          <h1>${activeNote.title || "Untitled Note"}</h1>
          <pre>${activeNote.body}</pre>
        </body>
      </html>
    `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const handleCut = () => {
    const focusedElement = document.activeElement;

    if (
      focusedElement instanceof HTMLInputElement ||
      focusedElement instanceof HTMLTextAreaElement
    ) {
      focusedElement.focus();

      try {
        document.execCommand("cut");
      } catch (err) {
        console.error("Failed to cut text: ", err);
      }
    }
  };

  const handleCopy = () => {
    const focusedElement = document.activeElement;

    if (
      focusedElement instanceof HTMLInputElement ||
      focusedElement instanceof HTMLTextAreaElement
    ) {
      const { selectionStart, selectionEnd, value } = focusedElement;
      const selectedText = value.substring(selectionStart, selectionEnd);

      const tempElement = document.createElement("div");
      tempElement.style.position = "absolute";
      tempElement.style.left = "-9999px";
      tempElement.style.top = "0";
      tempElement.innerHTML = selectedText;

      document.body.appendChild(tempElement);

      const range = document.createRange();
      range.selectNode(tempElement);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);

      try {
        document.execCommand("copy");
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }

      document.body.removeChild(tempElement);
    }
  };

  const handleDelete = () => {
    if (activeNote) {
      deleteNote(activeNote.id);
      refreshNotes();
    }
  };

  const handleSelectAll = () => {
    if (textareaRef.current) {
      const quill = textareaRef.current.getEditor();
      const length = quill.getLength();
      quill.setSelection(0, length);
      setIsSelected(!isSelected);
    }
  };

  const handleFindAndReplace = () => {
    let bodyText = activeNote.body;
    let searchPattern = findText;

    if (!matchCase) {
      searchPattern = new RegExp(searchPattern, "gi");
    } else {
      searchPattern = new RegExp(searchPattern, "g");
    }

    if (wholeWord) {
      searchPattern = new RegExp(
        `\\b${searchPattern.source}\\b`,
        searchPattern.flags
      );
    }

    const matches = [...bodyText.matchAll(searchPattern)];
    setFindCount(matches.length);

    const replacedText = bodyText.replace(searchPattern, replaceText);
    setReplaceCount(matches.length);
    handleNoteEdit(activeNote.title, replacedText);
    setShowFindReplaceModal(false);
  };

  const insertDateTime = () => {
    if (textareaRef.current) {
      const quill = textareaRef.current.getEditor();
      const range = quill.getSelection();
      const date = new Date();
      const dateString = date.toLocaleString();

      if (range) {
        const startPos = range.index;
        quill.insertText(startPos, dateString);
        quill.setSelection(startPos + dateString.length, 0);
        quill.focus();
      }
    }
  };

  const handleSpecialCharacterClick = () => {
    setShowSpecialCharacterModal(true);
  };

  const handleEmojiClick = () => {
    setShowEmojiModal(true);
  };

  const specialCharacters = [
    "©",
    "®",
    "™",
    "§",
    "¶",
    "•",
    "∞",
    "€",
    "£",
    "¥",
    "¢",
    "₹",
    "₣",
    "₩",
    "₽",
    "₪",
    "฿",
    "₲",
    "₼",
    "₿",
    "♠",
    "♣",
    "♥",
    "♦",
    "♂",
    "♀",
    "♪",
    "♫",
    "✈",
    "☀",
    "☁",
    "☂",
    "☃",
    "★",
    "☆",
    "✿",
    "☘",
    "☑",
    "✔",
    "✘",
    "✽",
    "✾",
    "✸",
    "✹",
    "✺",
    "✻",
    "✼",
  ];

  const insertSpecialCharacter = () => {
    if (textareaRef.current) {
      const quill = textareaRef.current.getEditor();
      const range = quill.getSelection();

      if (range && selectedSpecialCharacter) {
        quill.insertText(range.index, selectedSpecialCharacter);
        quill.setSelection(range.index + selectedSpecialCharacter.length, 0);
      }

      setShowSpecialCharacterModal(false);
      setSelectedSpecialCharacter("");
    }
  };

  const emojis = [
    "😊",
    "😂",
    "😍",
    "🤣",
    "😃",
    "😄",
    "😅",
    "😆",
    "😉",
    "😋",
    "😎",
    "🤗",
    "🤔",
    "🤨",
    "🤯",
    "😐",
    "😑",
    "😶",
    "🙄",
    "😏",
    "😣",
    "😥",
    "😮",
    "🤐",
    "😯",
    "😪",
    "😫",
    "😴",
    "🤤",
    "😵",
    "😲",
    "😳",
    "🥺",
    "😦",
    "😧",
    "😨",
    "😰",
    "😓",
    "🤩",
    "🤪",
    "🤫",
    "🤭",
    "🧐",
    "🤓",
    "😈",
    "👿",
    "👹",
    "👺",
    "🤡",
    "👻",
  ];

  const insertEmoji = () => {
    if (textareaRef.current) {
      const quill = textareaRef.current.getEditor();
      const range = quill.getSelection();

      if (range && selectedEmoji) {
        quill.insertText(range.index, selectedEmoji);
        quill.setSelection(range.index + selectedEmoji.length, 0);
      }

      setShowEmojiModal(false);
      setSelectedEmoji("");
    }
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.log(
          `Error attempting to enable full-screen mode: ${err.message}`
        );
      });
      setIsFullScreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch((err) => {
          console.log(
            `Error attempting to exit full-screen mode: ${err.message}`
          );
        });
        setIsFullScreen(false);
      }
    }
  };

  const toggleStatusBar = () => {
    setIsStatusBarVisible(!isStatusBarVisible);
  };

  const toggleSpellCheck = () => {
    setSpellCheckEnabled(!spellCheckEnabled);
  };

  useEffect(() => {
    const editorElement = $(textareaRef.current?.getEditor().root);

    if (spellCheckEnabled) {
      editorElement.attr("spellcheck", "true");
      editorElement.css("text-decoration", "none");
    } else {
      editorElement.attr("spellcheck", "false");
      editorElement.css("text-decoration", "none");
    }
  }, [spellCheckEnabled]);

  useEffect(() => {
    const editorElement = $(textareaRef.current?.getEditor().root);
    editorElement.attr("spellcheck", spellCheckEnabled ? "true" : "false");
  }, []);

  return (
    <>
      {!isFullScreen && <HeaderTextEditor toggleSidebar={toggleSidebar} />}

      <div
        className={`text-editor-container ${isFullScreen ? "fullscreen" : ""}`}
      >
        <div className="notes" id="app">
          <div className="displayFlex">
            <div
              className={`notes__sidebar ${
                isSidebarOpen ? "sidebar-open" : ""
              }`}
            >
              <div className="CreateNOteBTn">
                <button type="button" onClick={handleNoteAdd}>
                  + Create New
                </button>
              </div>

              <div className="notes__search mt-2">
                <input
                  ref={searchRef}
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="notes__list mt-2">{renderNoteList()}</div>
            </div>

            <div className="EditorContainer">
              <div className="ButtnCont">
                <div className="TexteditBtns">
                  <div className="btn-group" role="group" aria-label="Toolbar">
                    <Dropdown>
                      <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                        File
                        <img src={Down} alt="Down Arrow" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item href="#/new" onClick={handleNoteAdd}>
                          New
                        </Dropdown.Item>
                        <Dropdown.Item onClick={triggerFileInputClick}>
                          Open
                        </Dropdown.Item>
                        <Dropdown.Item
                          href="#/save-as"
                          className="SavUSSectio"
                          onClick={handleFileSave}
                        >
                          Save As
                        </Dropdown.Item>
                        <Dropdown.Item
                          href="#/print"
                          className="pntt"
                          onClick={handlePrint}
                        >
                          Print
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    <Dropdown>
                      <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                        Edit
                        <img src={Down} alt="Down Arrow" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={handleDelete}>
                          Delete
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setShowFindReplaceModal(true)}
                        >
                          Find & Replace
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    <Dropdown>
                      <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                        Insert
                        <img src={Down} alt="Down Arrow" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={insertDateTime}>
                          Date & Time
                        </Dropdown.Item>
                        <Dropdown.Item onClick={handleSpecialCharacterClick}>
                          Characters
                        </Dropdown.Item>
                        <Dropdown.Item onClick={handleEmojiClick}>
                          Emoji
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    <Dropdown>
                      <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                        Tools
                        <img src={Down} alt="Down Arrow" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={toggleSpellCheck}>
                          {spellCheckEnabled && (
                            <img
                              className="rightMarkImg"
                              src={rightMArk}
                              alt="Checkmark"
                            />
                          )}
                          Spell Check
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    <Dropdown>
                      <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                        View
                        <img src={Down} alt="Down Arrow" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={toggleStatusBar}
                          className="dropdown-item"
                        >
                          {isStatusBarVisible && (
                            <img
                              className="rightMarkImg"
                              src={rightMArk}
                              alt="Checkmark"
                            />
                          )}
                          Status Bar
                        </Dropdown.Item>
                        <Dropdown.Item onClick={toggleFullScreen}>
                          {isFullScreen && (
                            <img
                              className="rightMarkImg"
                              src={rightMArk}
                              alt="Checkmark"
                            />
                          )}
                          Full Screen
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>

                <div className="Ai_Ditectr">
                  <button
                    className="ai-detector-btn"
                    onClick={handleAiDetector}
                    disabled={
                      loading ||
                      !editorContent ||
                      stripHtmlTags(editorContent).trim().split(/\s+/).length <
                        5
                    }
                    style={{
                      opacity:
                        !editorContent ||
                        stripHtmlTags(editorContent).trim().split(/\s+/)
                          .length < 5
                          ? 0.5
                          : 1,
                      cursor:
                        loading ||
                        !editorContent ||
                        stripHtmlTags(editorContent).trim().split(/\s+/)
                          .length < 5
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    <BrainCircuit size={16} strokeWidth={1.5} />
                    {loading ? "Detecting..." : "AI Detector"}
                  </button>

                  {showResult && result && (
                    <div className="detection-result">
                      {result.isAI ? (
                        <span className="ai_ditected">
                          AI Detected: {result.probability}%
                        </span>
                      ) : (
                        <span className="No_ai_ditected">No AI Detected</span>
                      )}
                    </div>
                  )}
                  {error && <div className="error-message">{error}</div>}
                </div>
              </div>

              {renderNotePreview()}
            </div>
          </div>
        </div>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to delete this note?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={cancelDelete}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          className="FindReplace"
          show={showFindReplaceModal}
          onHide={() => setShowFindReplaceModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Find and Replace</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container-lg">
              <div className="row mb-3">
                <div className="col-3 d-flex align-items-center">
                  <label>Find</label>
                </div>
                <div className="col-9">
                  <input
                    type="text"
                    className="form-control"
                    value={findText}
                    onChange={(e) => setFindText(e.target.value)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-3 d-flex align-items-center">
                  <label>Replace</label>
                </div>
                <div className="col-9">
                  <input
                    type="text"
                    className="form-control"
                    value={replaceText}
                    onChange={(e) => setReplaceText(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleFindAndReplace}>
              Replace
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          className="sybolchooseModal"
          show={showSpecialCharacterModal}
          onHide={() => setShowSpecialCharacterModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Select Special Character</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="emojiCOnt">
              {specialCharacters.map((character, index) => (
                <button
                  key={index}
                  className={
                    selectedSpecialCharacter === character ? "active" : ""
                  }
                  onClick={() => setSelectedSpecialCharacter(character)}
                >
                  {character}
                </button>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="InsertEmogyBtn"
              variant="primary"
              onClick={insertSpecialCharacter}
            >
              Insert
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showEmojiModal}
          onHide={() => setShowEmojiModal(false)}
          className="sybolchooseModal2"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Emojis</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="emojiCOnt">
              {emojis.map((emoji, index) => (
                <button
                  key={index}
                  className={selectedEmoji === emoji ? "active" : ""}
                  onClick={() => setSelectedEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="InsertEmogyBtn"
              variant="primary"
              onClick={insertEmoji}
            >
              Insert
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showAiResultModal}
          onHide={() => {
            setShowAiResultModal(false);
            setShowResult(false);
          }}
          size="lg"
          centered
          className="ai-result-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>🤖 AI Detection Results</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {result && (
              <div className="ai-modal-content">
                <div className="ai-main-result">
                  <div
                    className={`ai-score-circle ${result.riskLevel?.toLowerCase()}`}
                  >
                    <div className="score-value">{result.probability}%</div>
                    <div className="score-label">AI Probability</div>
                  </div>
                  <div className="ai-verdict-section">
                    <h4>{result.verdict}</h4>
                    <span
                      className={`risk-badge-large risk-${result.riskLevel?.toLowerCase()}`}
                    >
                      {result.riskLevel} RISK
                    </span>
                    <div className="confidence-info">
                      Confidence Level: <strong>{result.confidence}%</strong>
                    </div>
                  </div>
                </div>

                <div className="ai-quick-stats">
                  <div className="stat-box">
                    <div className="stat-icon">📝</div>
                    <div className="stat-value">{result.wordCount}</div>
                    <div className="stat-label">Words</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-icon">📊</div>
                    <div className="stat-value">{result.sentenceCount}</div>
                    <div className="stat-label">Sentences</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-icon">🎯</div>
                    <div className="stat-value">
                      {result.foundPatterns.length}
                    </div>
                    <div className="stat-label">AI Patterns</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-icon">📁</div>
                    <div className="stat-value">
                      {result.analysis.categoryCount}
                    </div>
                    <div className="stat-label">Categories</div>
                  </div>
                </div>

                <div className="ai-detailed-analysis">
                  <details>
                    <summary>📊 View Detailed Analysis</summary>
                    <div className="analysis-content">
                      <div className="analysis-row">
                        <div className="analysis-col">
                          <h6>Linguistic Metrics</h6>
                          <div className="metric-item">
                            <span>Burstiness:</span>
                            <strong
                              className={
                                parseFloat(result.analysis.burstiness) < 0.35
                                  ? "warning"
                                  : "success"
                              }
                            >
                              {result.analysis.burstiness}
                            </strong>
                          </div>
                          <div className="metric-item">
                            <span>Lexical Diversity:</span>
                            <strong
                              className={
                                parseFloat(result.analysis.lexicalDiversity) <
                                0.5
                                  ? "warning"
                                  : "success"
                              }
                            >
                              {result.analysis.lexicalDiversity}
                            </strong>
                          </div>
                          <div className="metric-item">
                            <span>Perplexity:</span>
                            <strong
                              className={
                                parseFloat(result.analysis.perplexityScore) < 25
                                  ? "warning"
                                  : "success"
                              }
                            >
                              {result.analysis.perplexityScore}
                            </strong>
                          </div>
                        </div>
                        <div className="analysis-col">
                          <h6>Sentence Analysis</h6>
                          <div className="metric-item">
                            <span>Avg Length:</span>
                            <strong>
                              {result.analysis.avgSentenceLength} words
                            </strong>
                          </div>
                          <div className="metric-item">
                            <span>Std Deviation:</span>
                            <strong>{result.analysis.stdDeviation}</strong>
                          </div>
                          <div className="metric-item">
                            <span>Start Variety:</span>
                            <strong
                              className={
                                parseFloat(
                                  result.analysis.sentenceStartVariety
                                ) < 0.6
                                  ? "warning"
                                  : "success"
                              }
                            >
                              {result.analysis.sentenceStartVariety}
                            </strong>
                          </div>
                        </div>
                      </div>

                      <div className="analysis-row">
                        <div className="analysis-col">
                          <h6>Word Complexity</h6>
                          <div className="metric-item">
                            <span>Complex Words:</span>
                            <strong
                              className={
                                parseFloat(result.analysis.complexWordRatio) >
                                35
                                  ? "warning"
                                  : "success"
                              }
                            >
                              {result.analysis.complexWordRatio}
                            </strong>
                          </div>
                          <div className="metric-item">
                            <span>Very Complex:</span>
                            <strong>
                              {result.analysis.veryComplexWordRatio}
                            </strong>
                          </div>
                        </div>
                        <div className="analysis-col">
                          <h6>Pattern Density</h6>
                          <div className="metric-item">
                            <span>Transitions:</span>
                            <strong
                              className={
                                parseFloat(result.analysis.transitionDensity) >
                                8
                                  ? "warning"
                                  : "success"
                              }
                            >
                              {result.analysis.transitionDensity}
                            </strong>
                          </div>
                          <div className="metric-item">
                            <span>Adverbs:</span>
                            <strong
                              className={
                                parseFloat(result.analysis.adverbDensity) > 6
                                  ? "warning"
                                  : "success"
                              }
                            >
                              {result.analysis.adverbDensity}
                            </strong>
                          </div>
                        </div>
                      </div>

                      {result.foundPatterns.length > 0 && (
                        <div className="found-patterns-section">
                          <h6>
                            🔍 Detected AI Patterns (
                            {result.foundPatterns.length})
                          </h6>
                          <div className="patterns-list">
                            {result.foundPatterns
                              .slice(0, 15)
                              .map((pattern, idx) => (
                                <span key={idx} className="pattern-badge">
                                  {pattern}
                                </span>
                              ))}
                            {result.foundPatterns.length > 15 && (
                              <span className="pattern-badge more">
                                +{result.foundPatterns.length - 15} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>
                </div>

                {result.isAI && (
                  <div className="ai-recommendations">
                    <h6>💡 Recommendations</h6>
                    <ul>
                      <li>
                        Review highlighted words in yellow for AI-typical
                        patterns
                      </li>
                      <li>
                        Consider rewriting with more varied sentence structures
                      </li>
                      <li>Use more natural, conversational language</li>
                      <li>Add personal experiences or unique insights</li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAiResultModal(false);
                setShowResult(false);
              }}
            >
              Close
            </Button>
            {result && result.isAI && (
              <Button
                variant="primary"
                onClick={() => {
                  setShowAiResultModal(false);
                }}
              >
                View Highlights
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <input
          type="file"
          id="fileInput"
          style={{ display: "none" }}
          accept=".txt"
          onChange={handleFileOpen}
        />
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </div>
    </>
  );
};

export default TextEditor;